"use client";

import { useState } from "react";
import {
  Checkbox,
  Notice,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  RESULT_CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  CurveResults,
  FitFailure,
} from "@/components/tools/biology-lab/standard-curve-panels";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  type FitKind,
  fitCurve,
  parseSamples,
  parseStandards,
} from "@/lib/tool-kit/biology-lab/standard-curve";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const STANDARD_CURVE_CALCULATOR_STRINGS = {
  fitLabel: "Curve to fit",
  fitLinear: "Straight line (Bradford, BCA)",
  fit4pl: "Four-parameter logistic (ELISA)",
  standardsLabel: "Standards — concentration, response (one pair per line)",
  standardsHint:
    "Replicates go on their own lines. Use a zero standard for the blank.",
  standardsPlaceholder: "0, 0.06\n100, 0.52\n200, 0.98",
  exampleStandards:
    "0, 0.064\n0, 0.057\n15.6, 0.116\n15.6, 0.124\n31.25, 0.208\n31.25, 0.189\n62.5, 0.351\n62.5, 0.377\n125, 0.699\n125, 0.672\n250, 1.158\n250, 1.195\n500, 1.763\n500, 1.719\n1000, 2.158\n1000, 2.216",
  samplesLabel: "Unknowns — response, with an optional name before it",
  samplesHint: "For example “Serum 1, 0.412”, or just the number.",
  samplesPlaceholder: "0.412\nSerum 2, 0.905",
  exampleSamples:
    "Serum 1, 0.412\nSerum 2, 0.905\nSerum 3, 1.87\nSerum 4, 2.95",
  tryExample: "Try the example",
  clear: "Clear",
  unitLabel: "Concentration unit",
  unitPlaceholder: "pg/mL",
  exampleUnit: "pg/mL",
  responseLabel: "Response label",
  axisResponse: "Response",
  responsePlaceholder: "OD 450",
  exampleResponse: "OD 450",
  dilutionLabel: "Dilution factor of the unknowns",
  dilutionHint: "1 if read neat; 2 if you diluted 1 in 2 before the assay.",
  blankLabel: "Blank to subtract (optional)",
  blankHint: "Taken off every standard and unknown before fitting.",
  logAxis: "Logarithmic concentration axis",
  results: "Results",
  fitLinearName: "Least-squares line",
  fit4plName: "Four-parameter logistic fit",
  equationLinear: "y = {slope}x {sign} {intercept}",
  equation4pl: "y = {d} + ({a} − {d}) ÷ (1 + (x ÷ {c})^{b})",
  r2: "R²",
  points: "Points fitted",
  rmse: "Residual standard error",
  paramA: "a — response at zero",
  paramD: "d — response at infinity",
  paramC: "c — midpoint (EC50)",
  paramB: "b — slope factor",
  fourPointsNote:
    "Four standards fit four parameters exactly, so the curve passes through every point and R² says nothing. Use at least six standards, ideally in duplicate.",
  plotLabel:
    "Standard curve with {n} standards, the fitted curve and the unknowns",
  axisConcentration: "Concentration",
  samplesHeading: "Unknowns",
  colSample: "Sample",
  colReading: "Reading",
  colConc: "Concentration",
  colOriginal: "In the original sample",
  colStatus: "Check",
  unnamed: "Sample {n}",
  none: "—",
  statusWithin: "Within the standards",
  statusAbove: "Above the top standard — dilute and rerun",
  statusBelow: "Below the lowest standard — unreliable",
  statusUnreachable: "Outside the curve's range — no concentration",
  noSamples: "Add unknown readings to see their concentrations.",
  backHeading: "Standards back-calculated",
  colNominal: "Nominal",
  colStdReading: "Reading",
  colBack: "Back-calculated",
  colRecovery: "Recovery",
  backNote:
    "Recovery is the back-calculated concentration over the nominal one. Many assays accept 80–120%; the lowest standards are usually the least accurate.",
  copyTable: "Copy table",
  copied: "Copied!",
  skipped:
    "one: {count} line without a pair of numbers was skipped.|other: {count} lines without a pair of numbers were skipped.",
  errPoints: "A straight line needs at least three standards.",
  errDistinct:
    "The standards need different concentrations (four distinct values for the logistic fit, three of them above zero).",
  errFlat:
    "The responses do not change with concentration, so no curve can be fitted.",
  errFourPlPoints:
    "A four-parameter logistic fit needs at least four standards.",
  errFourPlFailed:
    "The logistic fit did not converge. Check the standards for a typo or a wrong column, or try the straight line.",
  errDilution: "The dilution factor must be 1 or more.",
  enter: "Enter the standards, one concentration and response per line.",
} as const;

export type StandardCurveCalculatorStrings = {
  [K in keyof typeof STANDARD_CURVE_CALCULATOR_STRINGS]: string;
};

const ID = "sc";

export default function StandardCurveCalculatorWidget() {
  const strings = useToolUi(STANDARD_CURVE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [kind, setKind] = useState<FitKind>("4pl");
  const [standards, setStandards] = useState<string>(strings.exampleStandards);
  const [samples, setSamples] = useState<string>(strings.exampleSamples);
  const [unit, setUnit] = useState<string>(strings.exampleUnit);
  const [response, setResponse] = useState<string>(strings.exampleResponse);
  const [dilution, setDilution] = useState("2");
  const [blank, setBlank] = useState("");
  const [logX, setLogX] = useState(true);

  const blankValue = parseMeasuredNumber(blank) ?? 0;
  const parsedStandards = parseStandards(standards);
  const parsedSamples = parseSamples(samples);
  const points = parsedStandards.points.map((p) => ({
    x: p.x,
    y: p.y - blankValue,
  }));
  const unknowns = parsedSamples.samples.map((s) => ({
    label: s.label,
    value: s.value - blankValue,
  }));
  const dilutionValue = parseMeasuredNumber(dilution);
  const fit = points.length > 0 ? fitCurve(kind, points) : null;
  const skipped = parsedStandards.skipped + parsedSamples.skipped;

  function loadExample() {
    setKind("4pl");
    setStandards(strings.exampleStandards);
    setSamples(strings.exampleSamples);
    setUnit(strings.exampleUnit);
    setResponse(strings.exampleResponse);
    setDilution("2");
    setBlank("");
    setLogX(true);
  }

  function clearAll() {
    setStandards("");
    setSamples("");
  }

  function body() {
    if (fit === null) {
      return <Notice>{strings.enter}</Notice>;
    }
    if ("error" in fit) {
      return <FitFailure code={fit.error} strings={strings} />;
    }
    if (dilutionValue === null || !(dilutionValue >= 1)) {
      return <Notice>{strings.errDilution}</Notice>;
    }
    return (
      <CurveResults
        dilution={dilutionValue}
        fit={fit}
        fmt={fmt}
        logX={logX}
        points={points}
        responseLabel={
          response.trim() === "" ? strings.axisResponse : response.trim()
        }
        samples={unknowns}
        strings={strings}
        unit={unit.trim()}
      />
    );
  }

  return (
    <>
      <PillGroup
        label={strings.fitLabel}
        name={`${ID}-fit`}
        onChange={setKind}
        options={[
          { value: "linear", label: strings.fitLinear },
          { value: "4pl", label: strings.fit4pl },
        ]}
        value={kind}
      />
      <div className="grid gap-4 md:grid-cols-2">
        <TextAreaField
          hint={strings.standardsHint}
          id={`${ID}-standards`}
          label={strings.standardsLabel}
          onChange={setStandards}
          placeholder={strings.standardsPlaceholder}
          rows={9}
          value={standards}
        />
        <TextAreaField
          hint={strings.samplesHint}
          id={`${ID}-samples`}
          label={strings.samplesLabel}
          onChange={setSamples}
          placeholder={strings.samplesPlaceholder}
          rows={9}
          value={samples}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <TextField
          id={`${ID}-unit`}
          label={strings.unitLabel}
          onChange={setUnit}
          placeholder={strings.unitPlaceholder}
          value={unit}
        />
        <TextField
          id={`${ID}-response`}
          label={strings.responseLabel}
          onChange={setResponse}
          placeholder={strings.responsePlaceholder}
          value={response}
        />
        <TextField
          hint={strings.dilutionHint}
          id={`${ID}-dilution`}
          label={strings.dilutionLabel}
          numeric
          onChange={setDilution}
          value={dilution}
        />
        <TextField
          hint={strings.blankHint}
          id={`${ID}-blank`}
          label={strings.blankLabel}
          numeric
          onChange={setBlank}
          value={blank}
        />
      </div>
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={loadExample}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button className={BUTTON_SECONDARY} onClick={clearAll} type="button">
          {strings.clear}
        </button>
      </div>
      <Checkbox checked={logX} label={strings.logAxis} onChange={setLogX} />
      {skipped > 0 ? (
        <Notice>
          {tPlural(strings.skipped, locale, skipped, { count: fmt(skipped) })}
        </Notice>
      ) : null}
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <div className={`${RESULT_CARD} space-y-4`}>{body()}</div>
      </section>
    </>
  );
}
